<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <div class="s-textarea"
       :class="$attrs.class"
       :style="$attrs.style"
  >
    <label
      class="s-textarea__label"
      :class="[{ 'text-gray-300': disabled || readonly }, hiddenLabel]"
      :aria-hidden="isScreenReaderActive ? true : undefined"
      :for="isScreenReaderActive ? undefined : inputId"
      :data-test="testId ? `label-${testId}` : null"
    >{{ labelText }}</label>

    <div
      class="s-textarea__container"
      :class="{ 's-textarea__container--auto-resize': maxHeight }"
      :style="maxHeight ? `--max-height: ${maxHeight}px` : null"
      :data-replicated-value="maxHeight ? modelValue : undefined"
      data-test="s-textarea__container"
    >
      <span
        ref="placeholder"
        class="s-textarea__placeholder"
        :class="{ 'd-none': !isPlaceholderVisible || isScreenReaderActive }"
        :data-test="testId ? `placeholder-${testId}` : null"
        tabindex="-1"
        aria-hidden="true"
      >{{ placeholder }}</span>
      <Field
        v-if="validationFieldName && validationRules"
        :validate-on-blur="doValidateOnBlur"
        :validate-on-input="doValidateOnInputChange"
        :label="validationFieldLabel"
        :name="validationFieldName"
        v-bind="filteredAttrsVeeValidate"
        v-slot="slotProps"
        :rules="validationRules"
        v-on="$listeners"
      >
        <b-form-textarea
          v-bind="{ ...slotProps.field, ...filteredAttrsVeeValidate }"
          :id="inputId"
          ref="textarea"
          :value="modelValue"
          class="s-textarea__textarea"
          :placeholder="undefined"
          :class="{ 's-textarea__textarea--invalid': isInvalid, 's-textarea__textarea--no-resize': noResize }"
          :disabled="disabled"
          :readonly="readonly"
          :aria-label="isScreenReaderActive ? localLabelText : undefined"
          :aria-invalid="isInvalid ? 'true' : undefined"
          :aria-describedby="descriptionId || $attrs['aria-describedby']"
          :data-test="testId ? `textarea-${testId}` : null"
          v-on="$listeners"
        />
      </Field>
      <b-form-textarea
        v-else
        v-bind="filteredAttrs"
        :id="inputId"
        ref="textarea"
        :value="modelValue"
        class="s-textarea__textarea"
        :placeholder="undefined"
        :class="{ 's-textarea__textarea--invalid': isInvalid, 's-textarea__textarea--no-resize': noResize }"
        :disabled="disabled"
        :readonly="readonly"
        :aria-label="isScreenReaderActive ? localLabelText : undefined"
        :aria-invalid="isInvalid ? 'true' : undefined"
        :aria-describedby="descriptionId || $attrs['aria-describedby']"
        :data-test="testId ? `textarea-${testId}` : null"
        v-on="$listeners"
        @input="$emit('update:modelValue', $event)"
      />
    </div>

    <!-- invalid text -->
    <p
      v-if="isInvalid"
      :id="`invalid-${inputId}`"
      class="s-textarea__invalid text-danger-dark nano mt-1 mb-0"
      :data-test="testId ? `invalid-${testId}` : null"
    >
      {{ invalidFieldMessage }}
    </p>

    <!-- help text -->
    <p
      v-if="helpText"
      :id="`help-${inputId}`"
      class="s-textarea__help text-gray-300 nano mt-1 mb-0"
      :data-test="testId ? `help-${testId}` : null"
    >
      {{ helpText }}
    </p>
  </div>
</template>

<script>
import { Field } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'
import { useFieldError, useIsFieldDirty } from 'vee-validate'

export default {
  // TODO VUE3: This is a mix of Options API and Composition API which should be removed by refactoring
  // the whole component to Composition API. Right now it's needed to access validation state of VeeValidate.
  setup(props) {
    // no validation -> dont initialise handlers
    if (!props.validationFieldName || !props.validationRules) {
      return {
        doValidateOnBlur: undefined,
        doValidateOnInputChange: undefined,
        validationErrorMessage: undefined,
      }
    }

    const validationErrorMessage = ref(undefined)
    const doValidateOnBlur = ref(false)
    const doValidateOnInputChange = ref(false)

    onMounted(() => {
      const isFieldDirty = useIsFieldDirty(props.validationFieldName)
      const hasFieldError = useFieldError(props.validationFieldName)

      // OnBlur validation: Only when field is dirty
      watch(isFieldDirty, (newVal) => {
        doValidateOnBlur.value = newVal
      })

      // InputChange validation: Only when field already has error (and user changes the value again)
      watch(hasFieldError, (newVal) => {
        doValidateOnInputChange.value = !!newVal
        validationErrorMessage.value = newVal
      })
    })

    return {
      doValidateOnBlur,
      doValidateOnInputChange,
      validationErrorMessage,
    }
  },

  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  // Prevent inheriting attributes from parent component to root element of this component.
  // This is to prevent having duplicate data-cy/test or aria attributes on the root element.
  inheritAttrs: false,

  name: 'STextarea',

  components: { Field },

  props: {
    modelValue: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    labelText: {
      type: String,
      default: '',
      required: true,
    },

    placeholder: {
      type: String,
      default: '',
    },

    // TODO: Remove this prop after design is ready. We are not allowed to hide the label, because of accessibility
    hideLabel: {
      type: Boolean,
      default: false,
    },

    // Not required to use for field validation. Only use this for fields that are not part of a form and validate
    // special use cases.
    invalidText: {
      type: String,
      default: '',
    },

    helpText: {
      type: String,
      default: '',
    },

    testId: {
      type: String,
      default: '',
    },

    noResize: {
      type: Boolean,
      default: false,
    },

    maxHeight: {
      type: Number,
      default: null,
    },

    /*
    Value should match a string in "validations.attributes" (see [LANG].json)
    Setting this prop will also render attribute name="validationFieldName" on the input element.
    Must be unique within the <VForm> tag!
    */
    validationFieldName: {
      type: String,
      default: '',
    },

    /*
    Rules to validate, example:
      - validation-rules="required|email"
    See all available rules in src/config/setup/plugins/veeValidate/index.js
    */
    validationRules: {
      type: [String, Object],
      default: '',
    },

    /*
    validationFieldName must be unique within the <VForm> tag. If that is not possible or if you need to translate
    a different attribute name, you can set this prop to a different value that exists in "validations.attributes".
     */
    validationFieldLabel: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    inputId: null,
    localLabelText: '',
    isPlaceholderVisible: true,
    isScreenReaderActive: false,
  }),

  computed: {
    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    filteredAttrsVeeValidate() {
      const { class: _cls, style: _style, name: _name, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    invalidFieldMessage() {
      return this.invalidText || this.validationErrorMessage
    },

    isInvalid() {
      return !!this.invalidText || !!this.validationErrorMessage
    },

    descriptionId() {
      // is invalid and has help text
      if (this.isInvalid && this.helpText) return `invalid-${this.inputId} help-${this.inputId}`

      // is invalid, but no help text
      if (this.isInvalid) return `invalid-${this.inputId}`

      // is valid and has help text
      if (this.helpText) return `help-${this.inputId}`

      // is valid and no help text
      return undefined
    },

    hiddenLabel() {
      return this.hideLabel ? 'd-none' : ''
    },
  },

  emits: ['update:modelValue'],

  methods: {
    toggleScreenReaderLabel() {
      if (!this.isScreenReaderActive) return

      // We need to hide the placeholder from screen readers by setting aria-hidden="true".
      // This is because our placeholders have mostly the same value as the label and a screen reader would read
      // it twice, confusing the user.
      // If placeholder and labelText are the same, we can just use the labelText as the label value.
      // If they are not the same, we concatenate them with a comma and use that as the label value.
      const isSameLabelPlaceholder = this.labelText === this.placeholder || !this.placeholder
      this.localLabelText = isSameLabelPlaceholder ? this.labelText : `${this.labelText}, ${this.placeholder}`
    },
  },

  watch: {
    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
      this.toggleScreenReaderLabel()
    },

    modelValue: {
      async handler(newVal, oldVal) {
        if (oldVal === newVal) return
        // eslint-disable-next-line
        this.isPlaceholderVisible = !!newVal ? !String(newVal)?.length : true;
      },

      immediate: true,
    },
  },

  async beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
    this.toggleScreenReaderLabel()
  },

  created() {
    this.inputId = this.$uuid()
  },
}
</script>

<style scoped lang="scss">
.s-textarea__label {
  font-size: pxtorem(15);
}

.s-textarea__placeholder {
  position: absolute;
  z-index: 1;
  top: 13px;
  left: 11px;
  color: $color-gray-300;
  pointer-events: none;
}

.s-textarea__textarea:disabled,
.s-textarea__textarea[readonly] {
  color: $color-gray-300;
  background-color: $color-white;
  box-shadow: none;
}

.s-textarea__textarea--invalid {
  border-color: $color-danger;
  background-color: $color-danger-light;

  &:hover {
    border-color: $color-danger;
  }
}

.s-textarea__textarea--no-resize {
  resize: none;
}

.s-textarea__container {
  position: relative;
}

// CSS Trick for auto-resize textarea
.s-textarea__container--auto-resize {
  display: grid;
  max-height: var(--max-height);

  &::after {
    // Note the weird space at the end! Needed to preventy jumpy behavior
    content: attr(data-replicated-value) ' ';

    // This is how textarea text behaves
    white-space: pre-wrap;
    word-break: break-word;

    // Hidden from view, and clicks
    visibility: hidden;
    pointer-events: none;

    // styles like border or padding needs to be in sync with textarea, so the box-size is the same (for correct height calculation)
    border: 1px solid transparent;
    padding: 12px 10px;
  }

  &::after,
  .s-textarea__textarea {
    grid-area: 1 / 1 / 2 / 2;
    max-height: var(--max-height);
  }
}
</style>
